import { useState } from "react";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";

const earningsData = [
  { month: "Oct", amount: 620 },
  { month: "Nov", amount: 980 },
  { month: "Dec", amount: 1240 },
  { month: "Jan", amount: 890 },
  { month: "Feb", amount: 1560 },
  { month: "Mar", amount: 2100 },
  { month: "Apr", amount: 1820 },
];

const transactions = [
  { id: "t1", type: "earning", desc: "Mindset Reset Masterclass — 3 enrollments", amount: "+$291", date: "Apr 12, 2026", status: "completed" },
  { id: "t2", type: "earning", desc: "Career Accelerator Blueprint — 2 enrollments", amount: "+$298", date: "Apr 10, 2026", status: "completed" },
  { id: "t3", type: "payout", desc: "Weekly payout to Chase Bank ****4821", amount: "-$450", date: "Apr 7, 2026", status: "completed" },
  { id: "t4", type: "earning", desc: "Mindset Reset Masterclass — 5 enrollments", amount: "+$485", date: "Apr 5, 2026", status: "completed" },
  { id: "t5", type: "purchase", desc: "Enrolled: Corporate Leadership Edge", amount: "-$249", date: "Apr 2, 2026", status: "completed" },
  { id: "t6", type: "payout", desc: "Weekly payout to Chase Bank ****4821", amount: "-$380", date: "Mar 31, 2026", status: "completed" },
];

const maxAmount = Math.max(...earningsData.map((d) => d.amount));

export default function PaymentPage() {
  const [activeTab, setActiveTab] = useState<"overview" | "bank" | "transactions" | "pricing">("overview");
  const [bankForm, setBankForm] = useState({ accountName: "", routingNumber: "", accountNumber: "", bankName: "", accountType: "checking" });
  const [bankLinked, setBankLinked] = useState(false);
  const [linkSuccess, setLinkSuccess] = useState(false);

  const handleBankChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setBankForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleLinkBank = (e: React.FormEvent) => {
    e.preventDefault();
    setBankLinked(true);
    setLinkSuccess(true);
    setTimeout(() => setLinkSuccess(false), 4000);
  };

  return (
    <div className="min-h-screen bg-[#F7F6F3]">
      <Navbar />

      {/* Hero */}
      <section className="relative pt-24 pb-16 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/d9128d1bc93f51bc4a44450b1055cd7f.jpg"
            alt="Payment"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/65 to-black/75" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 text-center">
          <span className="inline-block bg-teal-500/20 border border-teal-400/30 text-teal-300 text-xs font-semibold px-4 py-1.5 rounded-full mb-4">
            Payment &amp; Monetization
          </span>
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
            Turn Your Expertise Into Income
          </h1>
          <p className="text-white/70 text-lg max-w-xl mx-auto">
            Link your bank account, set your program prices, and start earning from your coaching work — weekly payouts, zero hassle.
          </p>
        </div>
      </section>

      <section className="py-12">
        <div className="max-w-7xl mx-auto px-6">

          {/* Summary Cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            {[
              { label: "Total Earned", value: "$9,210", icon: "ri-money-dollar-circle-line", color: "text-teal-600", bg: "bg-teal-50" },
              { label: "This Month", value: "$1,820", icon: "ri-bar-chart-line", color: "text-emerald-600", bg: "bg-emerald-50" },
              { label: "Pending Payout", value: "$589", icon: "ri-time-line", color: "text-amber-600", bg: "bg-amber-50" },
              { label: "Total Students", value: "94", icon: "ri-group-line", color: "text-rose-600", bg: "bg-rose-50" },
            ].map((s) => (
              <div key={s.label} className="bg-white rounded-2xl border border-gray-100 p-5 flex items-center gap-4">
                <div className={`w-12 h-12 flex items-center justify-center rounded-xl ${s.bg}`}>
                  <i className={`${s.icon} text-xl ${s.color}`} />
                </div>
                <div>
                  <p className="text-xl font-bold text-gray-900">{s.value}</p>
                  <p className="text-gray-400 text-xs">{s.label}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Tabs */}
          <div className="flex gap-1 bg-white border border-gray-100 rounded-xl p-1 mb-6 w-fit">
            {(["overview", "bank", "transactions", "pricing"] as const).map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`px-5 py-2 rounded-lg text-sm font-medium transition-all cursor-pointer whitespace-nowrap capitalize ${
                  activeTab === tab ? "bg-teal-600 text-white" : "text-gray-500 hover:text-gray-700"
                }`}
              >
                {tab === "bank" ? "Bank Account" : tab}
              </button>
            ))}
          </div>

          {/* Overview Tab */}
          {activeTab === "overview" && (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Earnings Chart */}
              <div className="lg:col-span-2 bg-white rounded-2xl border border-gray-100 p-6">
                <h2 className="font-semibold text-gray-900 mb-6">Earnings Overview</h2>
                <div className="flex items-end gap-3 h-48">
                  {earningsData.map((d) => (
                    <div key={d.month} className="flex-1 flex flex-col items-center gap-2">
                      <span className="text-xs text-gray-500">${d.amount}</span>
                      <div
                        className="w-full bg-teal-500 rounded-t-lg transition-all hover:bg-teal-400 cursor-pointer"
                        style={{ height: `${(d.amount / maxAmount) * 140}px` }}
                      />
                      <span className="text-xs text-gray-400">{d.month}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Payout Info */}
              <div className="bg-white rounded-2xl border border-gray-100 p-6">
                <h2 className="font-semibold text-gray-900 mb-5">Payout Schedule</h2>
                <div className="space-y-4">
                  <div className="flex items-center gap-3 p-3 bg-teal-50 rounded-xl">
                    <div className="w-8 h-8 flex items-center justify-center bg-teal-100 rounded-lg">
                      <i className="ri-bank-line text-teal-600" />
                    </div>
                    <div>
                      <p className="text-xs font-semibold text-gray-900">Chase Bank ****4821</p>
                      <p className="text-xs text-gray-400">Checking Account</p>
                    </div>
                    <span className="ml-auto text-xs bg-teal-500 text-white px-2 py-0.5 rounded-full">Active</span>
                  </div>
                  {[
                    { label: "Payout Frequency", value: "Weekly" },
                    { label: "Next Payout", value: "Apr 14, 2026" },
                    { label: "Pending Amount", value: "$589.00" },
                    { label: "Platform Fee", value: "10%" },
                  ].map((item) => (
                    <div key={item.label} className="flex justify-between text-sm">
                      <span className="text-gray-500">{item.label}</span>
                      <span className="font-semibold text-gray-900">{item.value}</span>
                    </div>
                  ))}
                </div>
                <button
                  onClick={() => setActiveTab("bank")}
                  className="w-full mt-5 py-2.5 border border-teal-200 text-teal-600 text-sm font-semibold rounded-xl hover:bg-teal-50 transition-colors cursor-pointer whitespace-nowrap"
                >
                  Manage Bank Account
                </button>
              </div>
            </div>
          )}

          {/* Bank Account Tab */}
          {activeTab === "bank" && (
            <div className="max-w-xl">
              <div className="bg-white rounded-2xl border border-gray-100 p-8">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 flex items-center justify-center bg-teal-100 rounded-xl">
                    <i className="ri-bank-line text-teal-600 text-xl" />
                  </div>
                  <div>
                    <h2 className="font-semibold text-gray-900">Link Bank Account</h2>
                    <p className="text-gray-400 text-xs">Receive weekly payouts directly to your bank</p>
                  </div>
                </div>

                {linkSuccess && (
                  <div className="bg-teal-50 border border-teal-200 rounded-xl p-4 mb-5 flex items-center gap-3">
                    <i className="ri-checkbox-circle-line text-teal-600 text-lg" />
                    <p className="text-teal-700 text-sm font-medium">Bank account linked successfully!</p>
                  </div>
                )}

                {/* Security badges */}
                <div className="flex gap-3 mb-6">
                  {[
                    { icon: "ri-shield-check-line", label: "256-bit SSL" },
                    { icon: "ri-lock-line", label: "Bank-level Security" },
                    { icon: "ri-eye-off-line", label: "Data Encrypted" },
                  ].map((b) => (
                    <div key={b.label} className="flex-1 flex flex-col items-center gap-1 bg-gray-50 rounded-xl p-3">
                      <i className={`${b.icon} text-teal-600`} />
                      <span className="text-xs text-gray-500 text-center">{b.label}</span>
                    </div>
                  ))}
                </div>

                <form onSubmit={handleLinkBank} className="space-y-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1.5">Account Holder Name</label>
                    <input
                      name="accountName"
                      value={bankForm.accountName}
                      onChange={handleBankChange}
                      placeholder="Full legal name"
                      required
                      className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-teal-400"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1.5">Bank Name</label>
                    <input
                      name="bankName"
                      value={bankForm.bankName}
                      onChange={handleBankChange}
                      placeholder="e.g. Chase, Bank of America"
                      required
                      className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-teal-400"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1.5">Account Type</label>
                    <select
                      name="accountType"
                      value={bankForm.accountType}
                      onChange={handleBankChange}
                      className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-teal-400 bg-white cursor-pointer"
                    >
                      <option value="checking">Checking</option>
                      <option value="savings">Savings</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1.5">Routing Number</label>
                    <input
                      name="routingNumber"
                      value={bankForm.routingNumber}
                      onChange={handleBankChange}
                      placeholder="9-digit routing number"
                      required
                      className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-teal-400"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1.5">Account Number</label>
                    <input
                      name="accountNumber"
                      value={bankForm.accountNumber}
                      onChange={handleBankChange}
                      placeholder="Account number"
                      required
                      className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-teal-400"
                    />
                  </div>
                  <button
                    type="submit"
                    className="w-full py-3 bg-teal-600 hover:bg-teal-700 text-white font-semibold rounded-xl transition-colors cursor-pointer whitespace-nowrap"
                  >
                    {bankLinked ? "Update Bank Account" : "Link Bank Account"}
                  </button>
                </form>
              </div>
            </div>
          )}

          {/* Transactions Tab */}
          {activeTab === "transactions" && (
            <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
              <div className="p-6 border-b border-gray-100">
                <h2 className="font-semibold text-gray-900">Transaction History</h2>
              </div>
              <div className="divide-y divide-gray-50">
                {transactions.map((tx) => (
                  <div key={tx.id} className="flex items-center gap-4 px-6 py-4">
                    <div className={`w-10 h-10 flex items-center justify-center rounded-xl flex-shrink-0 ${
                      tx.type === "earning" ? "bg-teal-50" : tx.type === "payout" ? "bg-amber-50" : "bg-rose-50"
                    }`}>
                      <i className={`${
                        tx.type === "earning" ? "ri-arrow-down-line text-teal-600" :
                        tx.type === "payout" ? "ri-bank-line text-amber-600" :
                        "ri-shopping-cart-line text-rose-600"
                      }`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">{tx.desc}</p>
                      <p className="text-xs text-gray-400">{tx.date}</p>
                    </div>
                    <div className="text-right">
                      <p className={`text-sm font-bold ${tx.amount.startsWith("+") ? "text-teal-600" : "text-gray-700"}`}>
                        {tx.amount}
                      </p>
                      <span className="text-xs bg-emerald-50 text-emerald-600 px-2 py-0.5 rounded-full">{tx.status}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Pricing Tab */}
          {activeTab === "pricing" && (
            <div>
              <h2 className="font-semibold text-gray-900 mb-6">Monetization Plans</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {[
                  {
                    name: "Starter",
                    price: "Free",
                    fee: "15% platform fee",
                    features: ["Up to 2 programs", "Community access", "Basic analytics", "Email support"],
                    cta: "Current Plan",
                    highlight: false,
                  },
                  {
                    name: "Coach Pro",
                    price: "$29/mo",
                    fee: "10% platform fee",
                    features: ["Unlimited programs", "Priority listing", "Advanced analytics", "Messaging tools", "Custom profile badge", "Priority support"],
                    cta: "Upgrade Now",
                    highlight: true,
                  },
                  {
                    name: "Elite",
                    price: "$79/mo",
                    fee: "5% platform fee",
                    features: ["Everything in Pro", "Featured placement", "Co-marketing opportunities", "Dedicated account manager", "Custom branding", "API access"],
                    cta: "Go Elite",
                    highlight: false,
                  },
                ].map((plan) => (
                  <div
                    key={plan.name}
                    className={`rounded-2xl border p-8 ${
                      plan.highlight
                        ? "border-teal-400 bg-teal-600 text-white"
                        : "border-gray-100 bg-white"
                    }`}
                  >
                    {plan.highlight && (
                      <span className="inline-block bg-white/20 text-white text-xs font-semibold px-3 py-1 rounded-full mb-4">
                        Most Popular
                      </span>
                    )}
                    <h3 className={`text-lg font-bold mb-1 ${plan.highlight ? "text-white" : "text-gray-900"}`}>
                      {plan.name}
                    </h3>
                    <p className={`text-3xl font-bold mb-1 ${plan.highlight ? "text-white" : "text-gray-900"}`}>
                      {plan.price}
                    </p>
                    <p className={`text-xs mb-6 ${plan.highlight ? "text-teal-200" : "text-gray-400"}`}>{plan.fee}</p>
                    <ul className="space-y-3 mb-8">
                      {plan.features.map((f) => (
                        <li key={f} className="flex items-center gap-2 text-sm">
                          <i className={`ri-check-line ${plan.highlight ? "text-teal-200" : "text-teal-500"}`} />
                          <span className={plan.highlight ? "text-white/90" : "text-gray-600"}>{f}</span>
                        </li>
                      ))}
                    </ul>
                    <button
                      className={`w-full py-3 rounded-xl font-semibold text-sm transition-colors cursor-pointer whitespace-nowrap ${
                        plan.highlight
                          ? "bg-white text-teal-600 hover:bg-teal-50"
                          : "border border-gray-200 text-gray-700 hover:bg-gray-50"
                      }`}
                    >
                      {plan.cta}
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>
      <Footer />
    </div>
  );
}
